import React, { ComponentType, ReactNode, SVGAttributes, useEffect, useMemo, useReducer, useRef } from 'react';

import { SearchParams } from '@/components/search/types';
import { getClientNameFromSearchType } from '@/components/search/v1/utils/helpers';

import styles from './SearchPanelList.module.css';

type SearchPanelListData = string[] | { description: string }[] | SearchParams[];

type SearchPanelListProps = {
  children?: ReactNode;
  searchValue?: string;
  isEmbedded?: boolean;
  showIcon?: boolean;
  centeredList?: boolean;
  data: SearchPanelListData;
  iconName?: ComponentType<SVGAttributes<SVGSVGElement>>;
  returnSelectedListItem?: ((item: SearchParams, index?: number) => void) | ((item: string, index?: number) => void);
  enterPressed?: boolean;
  onEnterPressed?: () => void;
  returnOnEnterKeyPressed?: boolean;
  dataIsAnArrayOfStrings?: boolean;
  searchPanelIsActive?: boolean;
  isFocused?: boolean;
  selectedListIndex?: number;
  setSelectedIndex?: React.Dispatch<React.SetStateAction<number>>;
};

export function SearchPanelList({
  isEmbedded,
  returnOnEnterKeyPressed,
  searchValue,
  iconName,
  showIcon = true,
  centeredList,
  data,
  returnSelectedListItem,
  enterPressed,
  onEnterPressed,
  dataIsAnArrayOfStrings,
  searchPanelIsActive,
  isFocused,
  children = undefined,
  selectedListIndex = undefined,
  setSelectedIndex = undefined,
  ref,
}: SearchPanelListProps & { ref?: React.Ref<React.ComponentRef<'div'>> }) {
  const listContainerRef = useRef(null);
  const IconComponent = iconName ?? null;

  const initialState = { selectedIndex: 0 };

  const reducer = (state, action) => {
    let selectedIndex: number | undefined;
    if (action.type === 'arrowUp') {
      selectedIndex = state.selectedIndex !== 0 ? state.selectedIndex - 1 : data.length - 1;
    }
    if (action.type === 'arrowDown') {
      selectedIndex = state.selectedIndex !== data.length - 1 ? state.selectedIndex + 1 : 0;
    }
    if (action.type === 'select') {
      selectedIndex = Number(action.payload);
    }
    if (selectedIndex !== undefined) {
      return { selectedIndex };
    }
    // Return default state
    return {
      selectedIndex: selectedListIndex,
    };
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const selectedIndex = useMemo(() => {
    return selectedListIndex ?? state.selectedIndex ?? 0;
  }, [selectedListIndex, state]);

  useEffect(() => {
    // Scroll the list container to keep the selected item in view
    const containerElement = listContainerRef.current as unknown as HTMLElement;

    if (containerElement) {
      const selectedItem = containerElement.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedItem) {
        const containerRect = containerElement.getBoundingClientRect();
        const itemRect = selectedItem.getBoundingClientRect();
        if (itemRect.bottom > containerRect.bottom) {
          // Selected item is below the visible area, scroll down
          containerElement.scrollTop += itemRect.bottom - containerRect.bottom;
        } else if (itemRect.top < containerRect.top) {
          // Selected item is above the visible area, scroll up
          containerElement.scrollTop -= containerRect.top - itemRect.top;
        }
      }
    }
  }, [selectedIndex]);

  /** Use v2 integration of setting the selected Item. */
  const handleItemClick = (item, index) => () => {
    if (setSelectedIndex) setSelectedIndex(index);
    dispatch({ type: 'select', payload: index });
    if (returnSelectedListItem) {
      returnSelectedListItem(item);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      dispatch({ type: 'select', payload: index });
      if (setSelectedIndex) setSelectedIndex(index);
    }
  };

  const handleMouseMove = (item) => {
    const index = data.indexOf(item);
    if (setSelectedIndex) setSelectedIndex(index);
    dispatch({ type: 'select', payload: index });
  };

  const getSearchValue = (searchValue, item) => {
    if (searchValue) return searchValue;
    if (item.fromValue && item.toValue) return `${item.fromValue} - ${item.toValue}`;
    return Array.isArray(item.searchValue) ? item.searchValue.join(', ') : item.searchValue;
  };

  const getKeyValue = (searchValue, item, index) => {
    if (dataIsAnArrayOfStrings) return item;
    if (searchValue) return `${searchValue}-${index}`;
    if (item.fromValue && item.toValue) return `${item.fromValue}-${item.toValue}-${index}`;
    return `${item.searchValue}-${index}`;
  };

  // use enterPressed/onEnterPressed to handle the enterKeypress from the parent component (as used in ExtendedSearch)
  useEffect(() => {
    // If there is no selected item or undefined, select the first item
    const selectedItem = data[selectedIndex]
      ? data[selectedIndex]
      : data[0] && dispatch({ type: 'select', payload: 0 });

    enterPressed && returnSelectedListItem?.(selectedItem as string & SearchParams);
    onEnterPressed?.();
  }, [data, enterPressed, onEnterPressed, returnSelectedListItem, selectedIndex]);

  // use returnOnEnterKeyPressed to use the enterKeyPressed from here (as used in CarouselButton)
  useEffect(() => {
    if (returnOnEnterKeyPressed && isFocused) {
      enterPressed && returnSelectedListItem?.(data[selectedIndex] as string & SearchParams);
    }
  }, [enterPressed, selectedIndex]);

  /**
   * From within the ExtendedSearch and CarouselButton context there will never be focus in the element itself.
   * Therefor this function does not really work, since parent controlled and the internal functions of this
   * component work well together its left here (It will work when the list has focus, but will never be focused after a user event).
   */
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (['ArrowUp', 'ArrowDown', 'Enter', 'NumpadEnter'].includes(event.key)) {
      const currentIndex = (event.target as HTMLLIElement)?.dataset.index;
      if (!currentIndex) return;
      if (event.key === 'ArrowUp') dispatch({ type: 'ArrowUp' });
      if (event.key === 'ArrowDown') dispatch({ type: 'ArrowDown' });
      if (event.key === 'Enter' || event.key === 'NumpadEnter') dispatch({ type: 'select', payload: currentIndex });
    }
  };

  return (
    <>
      {data.length > 0 && searchPanelIsActive ? (
        <div ref={ref}>
          <div
            className={isEmbedded ? styles.searchPanelListEmbeddedContainerStyle : styles.searchPanelListContainerStyle}
          >
            <ul
              ref={listContainerRef}
              className={isEmbedded ? styles.searchPanelEmbeddedListStyle : styles.searchPanelListStyle}
              onKeyDown={handleOnKeyDown}
            >
              {data.map((item, index) => (
                <li
                  style={{ justifyContent: centeredList ? 'center' : 'flex-start' }}
                  data-index={index}
                  data-is-selected={index === selectedIndex}
                  data-testid={`search-panel-list-item-${index}`}
                  onMouseMove={() => handleMouseMove(item)}
                  tabIndex={0}
                  key={getKeyValue(searchValue, item, index)}
                  onClick={handleItemClick(item, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={index === selectedIndex ? styles.selectedListItemStyle : styles.listItemStyle}
                >
                  {showIcon && IconComponent && (
                    <IconComponent className={styles.listItemIconStyle} width='20px' height='20px' aria-hidden='true' />
                  )}
                  {!dataIsAnArrayOfStrings && (
                    <>
                      {item.searchType && (
                        <div className={styles.searchTypeLabel}>
                          <span>{getClientNameFromSearchType(item.searchType).trim()}</span>
                        </div>
                      )}
                      <span>{getSearchValue(searchValue, item)}</span>
                      <span style={{ fontWeight: 'normal' }}>{item.description}</span>
                    </>
                  )}
                  {dataIsAnArrayOfStrings && <span>{item}</span>}
                </li>
              ))}
            </ul>
            {children}
          </div>
        </div>
      ) : null}
    </>
  );
}
